import React, { useCallback } from 'react';
import store from 'store/Store';
import productPageSOTBindings from 'analytics/bindingMethods/pages/productPage/productPageSOTBindings';
import anaConsts from 'analytics/constants';
import Flag from 'components/Flag/Flag';
import SwatchButton from 'components/ProductPage/Swatches/SwatchButton';
import SwatchChiclet from 'components/ProductPage/Swatches/SwatchChiclet';
import SwatchImage from 'components/ProductPage/Swatches/SwatchImage';
import SwatchPrice from 'components/ProductPage/Swatches/SwatchPrice';
import { Box, Grid, Text } from 'components/ui';
import { colors, space } from 'style/config';
import filterUtils from 'utils/Filters';
import marketingFlagsUtil from 'utils/MarketingFlags';
import skuUtils from 'utils/Sku';
import swatchUtils from 'utils/Swatch';
import userUtils from 'utils/User';
import { wrapFunctionalComponent } from 'utils/framework';
import UtilActions from 'utils/redux/Actions';
import Debounce from 'utils/Debounce';
const DEBOUNCE_HOVER = 50;
const { SWATCH_GROUP_VIEWS } = swatchUtils;
import helpersUtils from 'utils/Helpers';
const { deferTaskExecution } = helpersUtils;
import processEvent from 'analytics/processEvent';

const handleSkuOnMouseEnter = sku => {
    if (!Sephora.isTouch) {
        store.dispatch(UtilActions.merge('page.product', 'hoveredSku', sku));
    }
};

const handleSkuOnMouseLeave = () => {
    if (!Sephora.isTouch) {
        store.dispatch(UtilActions.merge('page.product', 'hoveredSku', null));
    }
};

const handleSkuOnMouseAction = Debounce.debounce((type, sku, isCustomSet, isItemSubModal) => {
    if (!isCustomSet && !isItemSubModal) {
        switch (type) {
            case 'enter':
                handleSkuOnMouseEnter(sku);

                break;
            case 'leave':
                handleSkuOnMouseLeave();

                break;
            default:
                break;
        }
    }
}, DEBOUNCE_HOVER);

const triggerAnalytics = params => {
    const {
        skuId,
        quantity,
        isOnlineOnly,
        listPrice,
        isSameDayEligibleSku,
        productId,
        storeId,
        topCategory,
        brandName,
        isOutOfStock,
        basketType,
        productName
    } = params;

    deferTaskExecution(() => {
        const shippingMethod = basketType ? basketType : anaConsts.DELIVERY_OPTIONS_MAP.Standard;
        const customerZipCode = userUtils.getZipCode();

        productPageSOTBindings.swatchChange({
            skuId,
            quantity,
            isOnlineOnly,
            listPrice,
            isSameDayEligibleSku,
            productId,
            storeId,
            topCategory,
            brandName,
            isOutOfStock,
            basketType,
            shippingMethod,
            customerZipCode
        });

        // Dispatches the Select Item Event
        processEvent.process(anaConsts.SELECT_ITEM_EVENT, {
            data: {
                listName: skuId || '',
                listId: skuId || '',
                productId: skuId || '',
                price: listPrice || '',
                brandName: brandName || '',
                productName: productName || '',
                productIndex: 0
            }
        });
    });
};

const handleSwatchSelection = (sku, props, isCustomSet, swatchItemCallback, skipAnalytics = false) => {
    const { isItemSubModal, onSwatchSelect, handleSelectSkuToEditClick } = props;

    if (isItemSubModal) {
        onSwatchSelect(sku);
    } else if (isCustomSet) {
        swatchItemCallback(sku);
    } else {
        const { handleSkuOnClick, updateDigitalDataProduct } = swatchUtils;

        // Critical UI updates first (synchronous)
        if (typeof handleSelectSkuToEditClick === 'function') {
            handleSelectSkuToEditClick(sku);
        } else {
            handleSkuOnClick(sku);
        }

        swatchItemCallback(sku);

        // Defer non-critical updates
        deferTaskExecution(() => {
            updateDigitalDataProduct(props);
        });
    }

    // Defer all analytics to prevent blocking the main thread
    deferTaskExecution(() => {
        if (skipAnalytics) {
            return;
        }

        const {
            isSameDayEligibleSku, isOutOfStock, isOnlineOnly, listPrice, productName, skuId
        } = sku;
        const {
            currentProduct: {
                productDetails: {
                    productId,
                    brand: { displayName: brandName }
                },
                parentCategory: { categoryId, displayName }
            },
            basketType,
            quantity,
            storeId
        } = props;

        triggerAnalytics({
            skuId,
            quantity,
            isOnlineOnly,
            listPrice,
            isSameDayEligibleSku,
            productId,
            storeId,
            topCategory: { categoryId, displayName },
            brandName,
            isOutOfStock,
            basketType,
            productName
        });
    });
};

/* eslint-disable-next-line complexity */
const SwatchItem = props => {
    const { isActiveSwatch, handleKeyDown, NO_MATCH } = swatchUtils;
    const isActive = props.isSkuReady && isActiveSwatch(props);
    const {
        currentProduct = {},
        sku,
        index,
        matchText,
        isShadeFinderActive,
        isCustomSet,
        swatchItemCallback,
        selectedItemRef,
        customSetDataAt,
        swatchView = SWATCH_GROUP_VIEWS.GRID,
        swatchTypeStyle,
        isFragrance,
        listPadding,
        isDropdown,
        colorIQMatch,
        isItemSubModal,
        hideMarketingFlags,
        hideInventoryStatus,
        skipAnalytics,
        hideSaleFlag,
        showHorizontalSizeSelector,
        sizeGroupName,
        isSkuDisabled
    } = props;

    const { skuSelectorType } = currentProduct;

    let match;

    if (isShadeFinderActive) {
        match = sku.skuId === isShadeFinderActive.skuId;
    } else if (!isShadeFinderActive && matchText && matchText.toLowerCase() === NO_MATCH) {
        match = false;
    } else {
        match = colorIQMatch?.skuId === sku.skuId;
    }

    const isImageType = !isFragrance && (isCustomSet || skuSelectorType === skuUtils.skuSwatchType.IMAGE);

    const newSku =
        matchText && matchText.toLowerCase() !== NO_MATCH
            ? {
                ...sku,
                match: 'match'
            }
            : sku;
    const isListView = swatchView === SWATCH_GROUP_VIEWS.LIST;
    const flags = marketingFlagsUtil.getShadeFilterFlags(isListView ? sku : newSku, isListView);

    const isDisabled = !!isSkuDisabled?.(sku);

    const interactiveButtonProps = {
        onClick: useCallback(
            () => handleSwatchSelection(sku, props, isCustomSet, swatchItemCallback, skipAnalytics),
            [sku.skuId, isCustomSet, props.isItemSubModal]
        ),
        // NOTE: Remove mouse events (onMouseEnter, onMouseLeave) and keyboard events(onFocus, onBlur) if product team allows and if possible - as it impacts the onClick INP if the user hovers and clicks on the swatch immediately
        onMouseEnter: useCallback(() => handleSkuOnMouseAction('enter', sku, isCustomSet, isItemSubModal), [sku.skuId, isCustomSet, isItemSubModal]),
        onMouseLeave: useCallback(() => handleSkuOnMouseAction('leave', sku, isCustomSet, isItemSubModal), [isCustomSet, isItemSubModal]),
        onFocus: useCallback(() => handleSkuOnMouseAction('enter', sku, isCustomSet, isItemSubModal), [sku.skuId, isCustomSet, isItemSubModal]),
        onBlur: useCallback(() => handleSkuOnMouseAction('leave', sku, isCustomSet, isItemSubModal), [isCustomSet, isItemSubModal]),
        onKeyDown: useCallback(e => handleKeyDown(e, index), [index])
    };

    const skuStatusPrefix = sku.isComingSoonTreatment ? 'Coming soon: ' : sku.isOutOfStock ? 'Out of stock: ' : isDisabled ? 'Unavailable: ' : null;
    const selectedSuffix = isActive ? ' - Selected' : null;

    const sharedButtonProps = {
        ref: isActive ? selectedItemRef : null,
        ['data-at']: Sephora.debug.dataAt(customSetDataAt || isActive ? 'selected_swatch' : 'swatch'),
        ['aria-selected']: isActive,
        ['aria-live']: 'polite',
        ['aria-atomic']: true,
        ['aria-disabled']: isDisabled || undefined,
        ['aria-label']: showHorizontalSizeSelector
            ? [skuStatusPrefix, sizeGroupName || sku.variationDesc || 'Standard size', ', ', sku.variationValue || sku.size, selectedSuffix].join('')
            : [skuStatusPrefix, sku.variationValue, sku.variationDesc ? ' ' + sku.variationDesc : null, selectedSuffix].join(''),
        ...(!isDisabled && interactiveButtonProps)
    };

    const swatchProps = {
        isDisabled,
        isOutOfStock: sku.isOutOfStock,
        isOnSale: !hideSaleFlag && sku.salePrice,
        isNew: sku.isNew,
        isMatch: match,
        hideInventoryStatus
    };

    const swatchImageProps = {
        src: sku.smallImage,
        type: swatchTypeStyle,
        hideInventoryStatus
    };

    if (showHorizontalSizeSelector) {
        const sizeLabel = sizeGroupName || sku.variationDesc;
        const sizeValue = sku.variationValue || sku.size;

        return (
            <SwatchButton
                {...sharedButtonProps}
                type='square'
                isActive={isActive}
                isImage={false}
                css={styles.horizontalSizeButton}
            >
                <SwatchChiclet
                    isActive={isActive}
                    {...swatchProps}
                    width='100%'
                    paddingY={2}
                    paddingX={2}
                    alignItems='center'
                    customCSS={styles.horizontalSizeChiclet}
                >
                    {swatchImageProps.src && (
                        <SwatchImage
                            {...swatchProps}
                            {...swatchImageProps}
                            isActive={isActive}
                            flexShrink={0}
                            hideInventoryStatus
                            css={styles.horizontalSizeImage}
                        />
                    )}
                    <Box
                        display='flex'
                        flexDirection='column'
                        alignItems='flex-start'
                        gap={1}
                        width='100%'
                    >
                        {sizeLabel && (
                            <Text
                                fontSize='xs'
                                lineHeight='tight'
                                textAlign='left'
                                fontWeight='bold'
                                children={sizeLabel}
                            />
                        )}
                        <Text
                            fontSize='xs'
                            lineHeight='tight'
                            textAlign='left'
                            css={styles.horizontalSizeValue}
                            children={sizeValue}
                        />
                    </Box>
                </SwatchChiclet>
            </SwatchButton>
        );
    }

    return swatchView === SWATCH_GROUP_VIEWS.LIST ? (
        <Box
            {...sharedButtonProps}
            is='div'
            display='flex'
            alignItems='center'
            css={{
                outline: 0,
                paddingLeft: listPadding,
                paddingRight: listPadding,
                paddingTop: 1,
                paddingBottom: 1,
                scrollSnapAlign: 'start',
                ...(!isDisabled && styles.interactiveButton),
                ...props.customCss?.swatchItem,
                ...(isActive && props.customCss?.swatchItem?.activeSwatchItem)
            }}
        >
            <SwatchImage
                {...swatchProps}
                {...swatchImageProps}
                hasOutline={true}
                isActive={isActive}
                marginRight={isDropdown ? 4 : 1}
                marginLeft={`-${swatchUtils.SWATCH_BORDER}px`}
                flexShrink={0}
                data-at={isActive ? Sephora.debug.dataAt('color_swatch_selected') : Sephora.debug.dataAt('color_swatch_option')}
            />
            <div data-at={Sephora.debug.dataAt('color_swatch_name')}>
                {filterUtils.getDescription(sku)}
                {props.showPrice && (
                    <SwatchPrice
                        sku={sku}
                        currentProduct={currentProduct}
                    />
                )}
                {!hideMarketingFlags &&
                    flags.map(({ text, backgroundColor }) => (
                        <Flag
                            key={text}
                            children={text}
                            backgroundColor={backgroundColor}
                            marginRight={1}
                        />
                    ))}
            </div>
        </Box>
    ) : (
        <SwatchButton
            {...sharedButtonProps}
            type={swatchTypeStyle}
            isActive={isActive}
            isImage={isImageType}
        >
            {isImageType && (
                <SwatchImage
                    {...swatchProps}
                    {...swatchImageProps}
                    data-at={customSetDataAt ? Sephora.debug.dataAt(`${customSetDataAt}_img`) : null}
                />
            )}

            {isImageType || (
                <>
                    <SwatchChiclet
                        isActive={isActive}
                        {...swatchProps}
                        {...(isFragrance && {
                            width: [198, null, '100%'],
                            height: null,
                            paddingY: 1,
                            paddingX: 2
                        })}
                        {...(props.showPrice && {
                            paddingY: 2
                        })}
                    >
                        {isFragrance ? (
                            <Grid
                                gap={2}
                                columns='auto 1fr'
                                alignItems='center'
                            >
                                <SwatchImage {...swatchImageProps} />
                                <Text
                                    fontSize='xs'
                                    lineHeight='tight'
                                    numberOfLines={3}
                                    css={{
                                        flex: 1,
                                        textTransform: 'uppercase'
                                    }}
                                    children={sku.variationValue}
                                />
                            </Grid>
                        ) : skuSelectorType === skuUtils.skuSwatchType.TEXT ? (
                            sku.swatchText
                        ) : (
                            sku.size
                        )}
                        {props.showPrice && (
                            <SwatchPrice
                                sku={sku}
                                currentProduct={currentProduct}
                            />
                        )}
                    </SwatchChiclet>
                </>
            )}
        </SwatchButton>
    );
};

const styles = {
    horizontalSizeButton: {
        width: 'auto',
        padding: `0 ${space[2]}px ${space[2]}px 0`
    },
    horizontalSizeChiclet: {
        minHeight: 52,
        '> span': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: space[2]
        }
    },
    interactiveButton: {
        transition: 'background-color .2s',
        '.no-touch &:hover, :focus': { backgroundColor: colors.nearWhite }
    }
};

SwatchItem.shouldUpdatePropsOn = [
    'sku',
    'currentProduct.currentSku',
    'isShadeFinderActive',
    'colorIQMatch',
    'showHorizontalSizeSelector',
    'sizeGroupName'
];

export default wrapFunctionalComponent(SwatchItem, 'SwatchItem');
